import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AvailableCustomer } from '../api/client';

interface CustomerPickerProps {
  /**
   * Customer id currently active on the session. Renders a "Current"
   * badge so the user can tell where they are without having to scan
   * for the highlight in the header dropdown.
   */
  activeCustomerId: string | null;
  /**
   * Customer id of the user's home tenant. Marked with a "Home" tag so
   * a global user can always find their way back to the tenant whose
   * email they signed in with.
   */
  homeCustomerId: string;
  /**
   * Called after a successful switch. Invoked AFTER the React Query
   * cache has been cleared, so consumers can immediately route the user
   * to fresh data without seeing the previous customer's cache.
   */
  onPicked?: (customer: AvailableCustomer) => void;
  /**
   * Layout variant. The landing page uses 'panel' (full card with
   * heading); the header dropdown uses 'menu' (compact list). Defaults
   * to 'panel'.
   */
  variant?: 'panel' | 'menu';
}

export function CustomerPicker({
  activeCustomerId,
  homeCustomerId,
  onPicked,
  variant = 'panel',
}: CustomerPickerProps) {
  const queryClient = useQueryClient();
  const [switching, setSwitching] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const customersQuery = useQuery({
    queryKey: ['available-customers'],
    queryFn: api.availableCustomers,
    // The customer set rarely changes within a session; let the React
    // Query staleTime keep us off the network for a couple of minutes.
    staleTime: 2 * 60_000,
  });

  const handlePick = async (customer: AvailableCustomer) => {
    if (switching) return;
    setError(null);
    setSwitching(customer.id);
    try {
      await api.setActiveCustomer(customer.id);
      // Drop every cached query for the previous customer; otherwise the
      // tree / page / audit views would briefly show stale content
      // belonging to the customer we just left.
      queryClient.clear();
      onPicked?.(customer);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch customer');
      setSwitching(null);
    }
  };

  if (customersQuery.isLoading) {
    return (
      <div className={containerClass(variant)}>
        <p className="text-sm text-wc-muted">Loading customers…</p>
      </div>
    );
  }

  if (customersQuery.isError || !customersQuery.data) {
    return (
      <div className={containerClass(variant)}>
        <p className="text-sm text-wc-red">Failed to load customers.</p>
      </div>
    );
  }

  const customers = customersQuery.data.customers;
  if (customers.length === 0) {
    return (
      <div className={containerClass(variant)}>
        <p className="text-sm text-wc-muted">No customers available.</p>
      </div>
    );
  }

  return (
    <div className={containerClass(variant)}>
      {variant === 'panel' && (
        <header className="mb-4">
          <h2 className="font-serif text-2xl text-wc-black">Pick a customer</h2>
          <p className="mt-2 text-sm text-wc-muted-strong">
            Choose whose data you want to view and work with. You can switch
            at any time from the header.
          </p>
        </header>
      )}
      <ul className={variant === 'panel' ? 'space-y-2' : 'max-h-80 space-y-1 overflow-auto'}>
        {customers.map((customer) => {
          const isActive = customer.id === activeCustomerId;
          const isHome = customer.id === homeCustomerId;
          const isSwitching = switching === customer.id;
          return (
            <li key={customer.id}>
              <button
                type="button"
                onClick={() => handlePick(customer)}
                disabled={switching !== null}
                className={`flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                  isActive
                    ? 'border-wc-blue bg-wc-blue/5 text-wc-blue'
                    : 'border-wc-muted/20 text-wc-black hover:border-wc-blue/40 hover:bg-wc-gray/60'
                } ${switching !== null && !isSwitching ? 'opacity-50' : ''}`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{customer.displayName}</span>
                  <span className="block truncate text-xs text-wc-muted-strong">
                    {customer.slug}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  {isActive && <TagPill tone="primary">Current</TagPill>}
                  {isHome && !isActive && <TagPill tone="muted">Home</TagPill>}
                  {isSwitching && (
                    <span className="text-xs text-wc-muted">Switching…</span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {error && (
        <p className="mt-3 text-xs text-wc-red" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function containerClass(variant: 'panel' | 'menu'): string {
  if (variant === 'panel') {
    return 'card w-full max-w-lg p-8';
  }
  return 'w-72 rounded-md border border-wc-muted/20 bg-wc-white p-3 shadow-lg';
}

interface TagPillProps {
  tone: 'primary' | 'muted';
  children: string;
}

function TagPill({ tone, children }: TagPillProps) {
  const cls =
    tone === 'primary'
      ? 'bg-wc-blue/10 text-wc-blue'
      : 'bg-wc-gray text-wc-muted-strong';
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}>
      {children}
    </span>
  );
}
